// Cross-surface pin drag (Session AEA): in the dashboard's customise mode, a page can be
// DRAGGED from the left menu onto the pinned-shortcuts block to pin it, and a pin chip can be
// dragged off the block to unpin it. The Director tried exactly this drag and nothing happened;
// the dropdown picker stays as the keyboard/touch path, but drag is now the primary interaction.
//
// DELIBERATELY NOT dnd-kit. The sidebar's nav reorder and the dashboard's block reorder are two
// separate DndContext trees (OfficeLayout and OfficeDashboard, either side of the router
// Outlet); truly joining them would mean hoisting one context over both and re-routing every
// drop -- a rebuild of the platform's most-used interaction (nav_order_set) to add a second
// one. Instead the pin drag is a native HTML5 drag layer that exists ONLY in customise mode:
//   - the drag SOURCE is the nav link itself (anchors are natively draggable already -- the
//     failed drag the Director attempted was this exact native drag with nowhere to land);
//     the dnd-kit grip handles keep the reorder, byte-untouched, so grip = reorder (always)
//     and row = pin (customise mode only) is the disambiguation;
//   - the drop TARGET is the pin editor inside the pinned-shortcuts block frame.
//
// The pieces here: the shared drag record (native dataTransfer payloads are unreadable during
// dragover, so the in-window record is what live drop feedback reads), the React context that
// tells the sidebar the dashboard is customising, and the pure drop decision (tested directly).
import { createContext, useContext } from "react";
import type { DragEvent as ReactDragEvent } from "react";
import { navItemByPath } from "./dashboardLayout";
import type { NavItem } from "./nav";

// The custom dataTransfer type marking a drag as ours (browsers lowercase types; keep it so).
export const PIN_DRAG_TYPE = "application/x-ele-pin";

export type PinDragPayload = { kind: "pin" | "unpin"; to: string };

// The in-window record of the active pin drag. Module-level, not React state: dragover events
// need it synchronously and no render depends on it directly (the drop zone keeps its own
// hover state). Cleared on dragend by the source AND by the zone's document-level listener,
// so a cancelled drag never leaves a stale record behind.
let current: PinDragPayload | null = null;

export function beginPinDrag(payload: PinDragPayload): void {
  current = payload;
}

export function endPinDrag(): void {
  current = null;
}

export function currentPinDrag(): PinDragPayload | null {
  return current;
}

// Whether the dashboard is in customise mode, published by OfficeDashboard and read by the
// sidebar (they sit either side of the router Outlet, so plain props cannot carry it). The
// default no-op value keeps OfficeDashboard renderable outside the office shell (tests).
export interface PinDragState {
  customising: boolean;
  setCustomising: (on: boolean) => void;
}

export const PinDragContext = createContext<PinDragState>({ customising: false, setCustomising: () => undefined });

export function usePinDrag(): PinDragState {
  return useContext(PinDragContext);
}

// What dropping a dragged page on the pin zone should do. Pure, so the reject reasons (the
// "clear feedback, never a silent no-op" requirement) are tested directly. A duplicate or an
// unpinnable page is REJECTED WITH A MESSAGE the zone shows live during the drag.
export type PinDropDecision =
  | { ok: true; to: string; label: string }
  | { ok: false; reason: "duplicate" | "not-pinnable"; message: string };

export function pinDropDecision(to: string, pins: string[]): PinDropDecision {
  const item = navItemByPath(to);
  if (!item) {
    return {
      ok: false,
      reason: "not-pinnable",
      message:
        to === "/office"
          ? "The Dashboard is your home screen, so it cannot be pinned to itself."
          : "That page cannot be pinned.",
    };
  }
  if (pins.includes(to)) {
    return { ok: false, reason: "duplicate", message: `${item.label} is already pinned.` };
  }
  return { ok: true, to, label: item.label };
}

// The drag-source props a nav link carries while the dashboard is customising. Outside
// customise mode this is empty, so the sidebar is BYTE-IDENTICAL in behaviour: the grip's
// dnd-kit reorder never sees these (the grip is a sibling of the link, not inside it), and a
// plain click still navigates. Every nav item drags -- validity is decided at the DROP ZONE
// with a visible reason (pinDropDecision), because a non-draggable item would be exactly the
// silent no-op this feature exists to remove.
export interface PinDragSourceProps {
  draggable?: boolean;
  title?: string;
  onDragStart?: (e: ReactDragEvent<HTMLElement>) => void;
  onDragEnd?: () => void;
}

export function pinDragSourceProps(item: NavItem, customising: boolean): PinDragSourceProps {
  // Outside customise mode the nav link must be EXPLICITLY non-draggable (Session AEH C4). An
  // <a href> is natively draggable by default, so returning nothing left the browser free to start
  // a native LINK drag -- a URL preview in the status bar, an OS drag ghost, landing nowhere. That
  // is exactly the failed drag the Director reported: he dragged a nav item that was not in
  // customise mode, and the browser's own link-drag fired instead of ours. draggable:false removes
  // that misleading affordance entirely; the dnd-kit reorder grip is a sibling driven by a pointer
  // sensor (not HTML5 drag), so nav reorder -- the platform's most-used action -- is untouched.
  if (!customising) return { draggable: false };
  return {
    draggable: true,
    title: `Drag onto the dashboard's Pinned shortcuts block to pin ${item.label}`,
    onDragStart: (e: ReactDragEvent) => {
      e.dataTransfer.setData(PIN_DRAG_TYPE, item.to);
      e.dataTransfer.effectAllowed = "copy";
      beginPinDrag({ kind: "pin", to: item.to });
    },
    onDragEnd: () => endPinDrag(),
  };
}
